import React from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@mui/material';
import {
  Language as LanguageIcon,
  Check as CheckIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'bg', name: 'Български', icon: <LanguageIcon /> },
  { code: 'en', name: 'English', icon: <PublicIcon /> },
];

interface LanguageSwitcherProps {
  isMobileMenu?: boolean;
}

export default function LanguageSwitcher({ isMobileMenu = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    handleClose();
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <Box>
      <Button
        id="language-button"
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        sx={{
          color: '#ffffff',
          textTransform: 'none',
          minWidth: isMobileMenu ? 'auto' : 'auto',
          px: isMobileMenu ? 2 : 1.5,
          py: isMobileMenu ? 1.5 : 0.5,
          borderRadius: isMobileMenu ? 2 : '50%',
          width: isMobileMenu ? '100%' : 36,
          height: isMobileMenu ? 'auto' : 36,
          background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.2) 0%, rgba(40, 160, 240, 0.1) 100%)',
          border: '1px solid rgba(40, 160, 240, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobileMenu ? 'flex-start' : 'center',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.3) 0%, rgba(40, 160, 240, 0.2) 100%)',
            borderColor: 'rgba(40, 160, 240, 0.6)',
            transform: isMobileMenu ? 'none' : 'scale(1.05)',
            boxShadow: '0 2px 8px rgba(40, 160, 240, 0.3)',
          },
        }}
      >
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#28A0F0',
          mr: isMobileMenu ? 1.5 : 0,
        }}>
          {currentLanguage.icon}
        </Box>
        {isMobileMenu && (
          <Box sx={{ 
            color: '#ffffff',
            fontWeight: 500,
            fontSize: '0.9rem',
          }}>
            {currentLanguage.name}
          </Box>
        )}
      </Button>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            border: '1px solid #2d2d44',
            '& .MuiMenuItem-root': {
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
              },
            },
          },
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={language.code === i18n.language}
          >
            <ListItemIcon sx={{ color: '#28A0F0', minWidth: 32 }}>
              {language.icon}
            </ListItemIcon>
            <ListItemText primary={language.name} />
            {language.code === i18n.language && (
              <CheckIcon sx={{ color: '#28A0F0', ml: 1 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
